import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Toolbar } from "primereact/toolbar";
import React, { useEffect, useRef, useState } from "react";
import { Search } from "../../shared/Search";
import { EmptyList, TFormState, TListState } from "../../shared/modal";
import { EmptyReqFilter, EmptyRequirement, JobTypeLabel, ReqPriorityLabel, ReqTermLabel, ReqTypeLabel, TReqFilter, TRequirement, TRequirementRecord, } from "./modal";
import { Fetch, ILazyParams } from "../../utilities/fetch";
import useToggle from "../../hooks/useToggle";
import { Form } from "./Form";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Filter } from "./Filter";
import { useAuth } from "../../hooks/useAuth";
import Submission from "./Submission";
import { Allocate } from "./Allocate";
import { format } from "date-fns";
import SubmissionTable from "./SubmissionTable";

type TL = TListState<TRequirement>;
type TF = TFormState<TRequirementRecord>;
type SF = TFormState<TRequirement | undefined>;

export const Table = () => {
    const { hasPermission } = useAuth();
    const canAdd = hasPermission('SalesMyRequirement', 'Add');
    const canEdit = hasPermission('SalesMyRequirement', 'Edit');
    const [table, setTable] = useState(EmptyList as TL);
    const [globalSearch, setGlobalSearch] = useState<string | null>(null);
    const dt = useRef<any>(null);
    const [lazyParams, setLazyParams] = useState<ILazyParams>({
        first: 0,
        rows: 25,
        page: 1,
        sortField: 'id',
        sortOrder: -1
    });
    const [filters, setFilters] = useState<TReqFilter>(EmptyReqFilter);

    const [form, setForm] = useState({ open: false, data: EmptyRequirement } as TF);
    const hideForm = () => setForm({ open: false, data: EmptyRequirement });

    const [allocation, setAllocation] = useState({ open: false, data: undefined } as SF);
    const hideAllocation = () => setAllocation({ open: false, data: undefined });

    const [submissions, setSubmissions] = useState({ open: false, data: undefined } as SF);
    const hideSubmissions = () => setSubmissions({ open: false, data: undefined });

    const [r, reload] = useToggle();

    const onPage = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const onSort = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const loadTableData = async () => {
        setTable(prev => ({ ...prev, loading: true }));
        const result = await Fetch<TL>("SalesMyRequirement",
            {
                query: new URLSearchParams({
                    lazyParams: JSON.stringify(lazyParams),
                    filters: JSON.stringify({
                        ...filters,
                        fromDate: filters.fromDate ? format(filters.fromDate as Date, 'yyyy-MM-dd') : null,
                        toDate: filters.toDate ? format(filters.toDate as Date, 'yyyy-MM-dd') : null
                    }),
                    ...(globalSearch ? { search: globalSearch } : {})
                }).toString()
            });
        if (result) {
            setTable({ data: result.data, count: result.count, loading: false });
        }
        setTable(prev => ({ ...prev, loading: false }));
    }


    useEffect(() => {
        loadTableData();
    }, [globalSearch, lazyParams, filters, r]);

    const openNew = () => {
        setForm({ open: true, data: EmptyRequirement });
    };

    const openEdit = (r: TRequirement) => {
        const data: TRequirementRecord = {
            ...r,
            state: { id: r.stateId ?? 0, name: r.stateName ?? "" },
            city: { id: r.cityId ?? 0, name: r.cityName ?? "" },
            clientCompany: { id: r.vendorCompanyId, name: r.vendorCompanyName },
            visa: r.visa?.map(s => s.id) ?? [],
            sVisa: r.visa,
        };
        setForm({ open: true, data: data });
    };

    const leftToolbarTemplate = () => {
        return (canAdd &&
            <React.Fragment>
                <div className="my-2">
                    <Button label="New" icon="pi pi-plus" className="p-button-success mr-2" onClick={openNew} />
                </div>
            </React.Fragment>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <React.Fragment>
                <Button label="Export" icon="pi pi-upload" className="p-button-help" onClick={exportCSV} />
            </React.Fragment>
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">Manage Requirements</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <Search setSearch={setGlobalSearch} />
            </span>
        </div>
    );

    const actionBodyTemplate = (rowData: TRequirement) => {
        return (
            <div className="actions">
                {canEdit && <Button icon="pi pi-pencil" className="p-button-rounded p-button-success p-button-text mr-1" onClick={() => openEdit(rowData)} />}
            </div>
        );
    };

    return (
        <>
            <Accordion>
                <AccordionTab header="Filter">
                    <Filter filters={filters} setFilters={(filters: TReqFilter) => setFilters(filters)} loading={table.loading} />
                </AccordionTab>
            </Accordion>
            <Toolbar className="mb-1 py-0" left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>
            <DataTable style={{ border: '1px solid rgba(105,105,105,0.17)', borderTopWidth: "0" }}
                ref={dt} size="small"
                value={table.data} lazy dataKey="id"
                paginator first={lazyParams.first} rows={lazyParams.rows} rowsPerPageOptions={[25, 50, 100, 200]}
                totalRecords={table.count} onPage={onPage}
                onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
                emptyMessage="No Requirement found."
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                header={header}
                loading={table.loading}
            >
                <Column field="id" header="Req ID"
                    body={(r: TRequirement) => (
                        <span onClick={() => setAllocation({ open: true, data: r, })}
                            title="Manage allocation"
                            className="clickable-id">
                            #{r.id}
                        </span>
                    )}
                />
                <Column field="postedBy" header="Sales Manager" />
                <Column field="jobTitle" header="Job Title" />
                {/* <Column field="reqType" header="Req Type" body={(r: TRequirement) => ReqTypeLabel[r.reqType]} /> */}
                <Column field="duration" header="Duration" body={(r: TRequirement) => `${r.duration} month(s)`} />
                <Column field="location" header="Location" body={(r: TRequirement) => r.cityId ? `${r.cityName}, ${r.stateName}, ${r.countryName}` : '--'} />
                <Column field="skillCategory" header="Skill Category" />
                <Column field="term" header="Term" body={(r: TRequirement) => ReqTermLabel[r.term]} />
                <Column field="jobType" header="Job Type" body={(r: TRequirement) => JobTypeLabel[r.jobType]} />
                <Column field="priority" header="Priority" body={(r: TRequirement) => ReqPriorityLabel[r.priority]} />
                <Column field="postedOn" header="Posted On" body={(r: TRequirement) => format(new Date(r.postedOn + 'z'), "MM-dd-yyyy hh:mm a")} />
                <Column
                    field="allocations"
                    header="Recruiters"
                    body={(r: TRequirement) => (
                        <div
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '6px',
                                alignItems: 'center',
                            }}
                        >
                            {r.allocations?.map((a) => {
                                const submissionCount =
                                    r.submissions?.filter(
                                        s => s.recruiterId === a.id
                                    ).length || 0;

                                return (
                                    <div
                                        key={a.id}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '4px 8px',
                                            background: '#f8f9fa',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '16px',
                                            fontSize: '13px',
                                            color: '#334155',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <span style={{ fontWeight: 500 }}>
                                            {a.name}
                                        </span>

                                        <span
                                            style={{
                                                minWidth: '20px',
                                                height: '20px',
                                                padding: '0 5px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: '10px',
                                                background: '#e2e8f0',
                                                color: '#475569',
                                                fontSize: '11px',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {submissionCount}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                />
                <Column
                    field="submissions"
                    header="Submittals"
                    body={(r: TRequirement) => {
                        const count = r.submissions?.length ?? 0;

                        return (
                            <span
                                onClick={() => {
                                    if (count > 0) {
                                        setSubmissions({
                                            open: true,
                                            data: r,
                                        });
                                    }
                                }}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '28px',
                                    height: '26px',
                                    padding: '0 9px',
                                    borderRadius: '13px',
                                    background: count > 0 ? '#eff6ff' : '#f8fafc',
                                    border: `1px solid ${count > 0 ? '#bfdbfe' : '#e2e8f0'
                                        }`,
                                    color: count > 0 ? '#2563eb' : '#94a3b8',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    cursor: count > 0 ? 'pointer' : 'default',
                                    transition: 'all 0.15s ease',
                                }}
                                title={count > 0 ? 'View submittals' : 'No submittals'}
                            >
                                {count}
                            </span>
                        );
                    }}
                />
                <Column field="vendorCompanyName" header="Client Company" />
                <Column field="contactPersonName" header="Contact Person" />
                <Column field="isHold" header="Status" body={(r: TRequirement) => r.isHold ? 'Hold' : 'Unhold'} />
                {/* <Column field="industryName" header="Industry" />
                <Column field="interviewProcess" header="Interview Process" />
                <Column field="visa" header="Visa" body={(r: TRequirement) => <span>{r.visa?.map(v => v.name).join(', ')}</span>} /> */}
                <Column header="Action" body={actionBodyTemplate} />
            </DataTable>
            {(canAdd || canEdit) && <Form open={form.open} data={form.data} hideDialog={hideForm} reload={reload} />}
            {canEdit && <SubmissionTable open={submissions.open} data={submissions.data} hideDialog={hideSubmissions} />}
            {canEdit && <Allocate open={allocation.open} data={allocation.data} hideDialog={hideAllocation} reload={reload} />}
        </>
    );
}
